import { Page, View, Text, Image, Link } from '@react-pdf/renderer';
import { styles } from './styles';
import {
  PublicEventDetailsResponse,
  PublicSessionItem,
} from '@hubs101/booking-api-skd-client/lib/types';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import weekday from 'dayjs/plugin/weekday';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { htmlToTextPDF } from '@utils/htmlParser';
import { globalStyles } from '../../styles';
import { useMemo } from 'react';
import { getSessionTimeWithoutTimezonePDF } from '@utils/time';
import { truncateText } from '@composites/PDF/utils';

import { iconsMap } from '@composites/PDF/constants';
import { APP_HOST } from '@utils/apiHooks';

// Extend Day.js with needed plugins
dayjs.extend(utc);
dayjs.extend(weekday);
dayjs.extend(advancedFormat);

const MAX_SPEAKER_NUMBER = 11;
const MAX_LINE_LENGTH = 130;

export interface AgendaPagesProps {
  event: PublicEventDetailsResponse;
  agendaPdfDates: string[];
  groupedAgendasSessions: PublicSessionItem[][];
  header: JSX.Element;
  footer: JSX.Element;
}

const AgendaPages = ({
  event,
  agendaPdfDates,
  groupedAgendasSessions,
  header,
  footer,
}: AgendaPagesProps) => {
  const { t } = useTranslation();

  const sortedGroupedAgendasSession = useMemo(() => {
    if (!groupedAgendasSessions) return [];

    return [...groupedAgendasSessions].sort((a, b) => {
      const dateA = new Date(a?.[0]?.start_date).getTime();
      const dateB = new Date(b?.[0]?.start_date).getTime();
      if (dateA < dateB) return -1;
      if (dateA > dateB) return 1;

      // If start_date is equal, sort by priority (lowest first)
      const priorityA =
        typeof a?.[0]?.priority === 'number' ? a?.[0]?.priority : 9999;
      const priorityB =
        typeof b?.[0]?.priority === 'number' ? b?.[0]?.priority : 9999;
      return priorityA - priorityB;
    });
  }, [groupedAgendasSessions]);

  const buttonURL = `${APP_HOST}/events/${event?.slug}${t('book-now-url')}`;

  function getDescription(session: PublicSessionItem) {
    const speakersLength = session?.speakers?.length ?? 0;
    const availableLines = 12 - (speakersLength + 1);

    const availableDescriptionChars = availableLines * MAX_LINE_LENGTH;

    return truncateText(
      htmlToTextPDF(session?.description) ?? '',
      availableDescriptionChars
    );
  }

  function isToMuchInformation(session: PublicSessionItem) {
    const descriptionLength =
      htmlToTextPDF(session?.description ?? '')?.length ?? 0;
    const speakersLength = session?.speakers?.length ?? 0;
    const availableLines = 12 - (speakersLength + 1);

    return (
      descriptionLength > availableLines * MAX_LINE_LENGTH ||
      speakersLength > MAX_SPEAKER_NUMBER
    );
  }

  return (
    <Page style={styles.page}>
      {header}

      <View style={styles.contentWrapper}>
        <Text
          style={{
            ...styles.title,
            color: event?.design.themeColor,
          }}
        >
          {t('agenda')} {new Date(event?.start_date ?? '').getFullYear()}
        </Text>

        <View
          style={{
            ...globalStyles.lineBar,
            borderColor: event?.design?.themeColor ?? 'black',
            marginLeft: 0,
            marginBottom: 20,
            width: 89,
          }}
        />

        <View style={styles.bodyContainer}>
          {agendaPdfDates?.map((date, index) => {
            const [day, restDate] = date.split(/\s*-\s*/);

            return (
              <View key={date} style={styles.dayWrapper}>
                <Text
                  style={{
                    ...styles.dayHeader,
                    backgroundColor: event?.design.themeColor,
                  }}
                >
                  <Text style={styles.day}>{day}</Text>
                  <Text> - </Text>
                  <Text style={styles.dateRest}>{restDate}</Text>
                </Text>

                {sortedGroupedAgendasSession?.[index]
                  ?.filter(session => session?.title)
                  ?.map(session => {
                    const description = getDescription(session);
                    const hasDescription = description?.length > 0;
                    const hasLocation = session?.location?.name?.length > 0;

                    return (
                      <View
                        style={styles.sessionWrapper}
                        key={session?.id}
                        wrap={false}
                      >
                        <View style={styles.sessionLeftColumn} wrap={false}>
                          <View
                            style={{
                              ...styles.sessionTimeWrapper,
                              backgroundColor: event?.design.themeColor,
                              borderColor: event?.design.themeColor,
                            }}
                          >
                            <Text style={styles.sessionTimeText}>
                              {`${getSessionTimeWithoutTimezonePDF(
                                session?.start_date,
                                session?.end_date,
                                event?.language
                              )}`}
                            </Text>
                          </View>

                          {hasLocation && (
                            <View
                              style={{
                                ...styles.sessionLocationWrapper,
                                backgroundColor: event?.design.themeColor,
                                borderColor: event?.design.themeColor,
                              }}
                            >
                              <Text
                                style={{
                                  ...styles.icon,
                                }}
                              >
                                {iconsMap.location}
                              </Text>

                              <Text style={styles.sessionLocationText}>
                                {truncateText(session?.location?.name, 110)}
                              </Text>
                            </View>
                          )}
                        </View>

                        <View style={styles.sessionRightColumn} wrap={false}>
                          <View
                            style={{
                              ...styles.sessionTitleWrapper,
                              borderColor: event?.design.themeColor,
                            }}
                          >
                            <Text style={styles.sessionTitle}>
                              {truncateText(session?.title, MAX_LINE_LENGTH)}
                            </Text>
                          </View>

                          {(hasLocation || hasDescription) && (
                            <View
                              style={{
                                ...styles.sessionDescriptionWrapper,
                                borderColor: event?.design.themeColor,
                                minHeight: hasDescription ? 0 : 22,
                              }}
                            >
                              <View
                                style={{
                                  marginBottom: session?.speakers?.length
                                    ? 5
                                    : 0,
                                }}
                              >
                                {session?.speakers
                                  ?.filter(
                                    speaker =>
                                      speaker?.first_name && speaker?.last_name
                                  )
                                  ?.map(speaker => {
                                    return (
                                      <Text key={speaker?.profile}>
                                        <Text style={styles.speakerName}>
                                          {speaker.first_name}{' '}
                                          {speaker.last_name}
                                        </Text>
                                        <Text style={styles.speakerDetails}>
                                          {' '}
                                          - {speaker?.job?.title ?? ''},{' '}
                                          {speaker?.company?.name ?? ''}
                                        </Text>
                                      </Text>
                                    );
                                  })}
                              </View>

                              <Text style={styles.sessionDescription}>
                                {description}
                              </Text>

                              {isToMuchInformation(session) && (
                                <View
                                  style={{
                                    ...styles.furtherInformationWrapper,
                                  }}
                                >
                                  <Text
                                    style={{
                                      ...styles.icon,
                                      color: event?.design?.themeColor,
                                      fontSize: 8,
                                    }}
                                  >
                                    {iconsMap.external_link_line}
                                  </Text>
                                  <Link
                                    href={`${APP_HOST}/events/${event?.slug}${t('agenda-url')}`}
                                    style={{
                                      ...styles.furtherInformationLink,
                                      color:
                                        event?.design?.themeColor ?? 'black',
                                    }}
                                  >
                                    {t('further-information')}
                                  </Link>
                                </View>
                              )}
                            </View>
                          )}
                        </View>
                      </View>
                    );
                  })}
              </View>
            );
          })}
        </View>

        <View style={styles.imagesWrapper}>
          <Image
            key={`image-1`}
            src={`https://media.we-conect.com/website/portfolios/${event?.portfolio?.slug}/event_guide_speakers_1.png`}
            style={{
              ...styles.colImage,
              borderTopLeftRadius: 4,
              borderBottomLeftRadius: 4,
            }}
          />
          <Image
            key={`image-2`}
            src={`https://media.we-conect.com/website/portfolios/${event?.portfolio?.slug}/event_guide_speakers_2.png`}
            style={{ ...styles.colImage }}
          />
          <Image
            key={`image-3`}
            src={`https://media.we-conect.com/website/portfolios/${event?.portfolio?.slug}/event_guide_speakers_3.png`}
            style={{ ...styles.colImage }}
          />
          <Image
            key={`image-4`}
            src={`https://media.we-conect.com/website/portfolios/${event?.portfolio?.slug}/event_guide_speakers_4.png`}
            style={{
              ...styles.colImage,
              borderTopRightRadius: 4,
              borderBottomRightRadius: 4,
            }}
          />

          <View
            style={{
              ...styles.buttonWrapper,
              backgroundColor: event?.design?.themeColor ?? 'black',
              color: 'white',
            }}
          >
            <Link href={buttonURL} style={styles.linkText}>
              {t('book-now')}
            </Link>
          </View>
        </View>
      </View>

      {footer}
    </Page>
  );
};

export default AgendaPages;
